import {create} from 'zustand';

import {mediaCompareViewportChanged} from '../../../services/analytics/productEvents';
import {type Zoom} from '../controls/ZoomControl';
import {type ViewportCenterPercent} from '../hooks/useScrollCenterPan';

const CENTERED_VIEWPORT: ViewportCenterPercent = {x: 50, y: 50};

export type CompareViewport = {
  zoom: Zoom;
  isEligible: boolean;
  isSynced: boolean;
  /**
   * Shared pan as the content fraction at the viewport center, so tiles of
   * different sizes derive their own scroll offsets and clamp at their own
   * edges.
   */
  center: ViewportCenterPercent;
};

export type CompareViewportInfo = Pick<
  CompareViewport,
  'isEligible' | 'isSynced'
>;

export const FIT_VIEWPORT: CompareViewport = {
  zoom: 'fit',
  isEligible: false,
  isSynced: false,
  center: CENTERED_VIEWPORT,
};

type CompareViewportState = {
  viewportsByPanelId: Partial<Record<string, CompareViewport>>;
  /** The last-activated synced panel owns the zoom keyboard shortcuts. */
  activePanelId: string | null;
  activePanelOwnerId: string | null;
  projectKey: string | null;
  setActivePanel: (panelId: string, ownerId: string) => void;
  releaseActivePanel: (ownerId: string) => void;
  setZoom: (panelId: string, zoom: Zoom) => void;
  setViewportInfo: (panelId: string, info: CompareViewportInfo) => void;
  setCenter: (panelId: string, center: ViewportCenterPercent) => void;
  resetViewport: (panelId: string) => void;
  resetProject: (projectKey: string) => void;
};

export const selectViewport =
  (panelId: string) =>
  (state: CompareViewportState): CompareViewport =>
    state.viewportsByPanelId[panelId] ?? FIT_VIEWPORT;

// Menu steps and repeated shortcut presses arrive in bursts and drag pans write per
// scroll event, so each panel reports once per settled burst: the viewport
// the user landed on, not every intermediate value.
const VIEWPORT_CHANGED_DEBOUNCE_MS = 500;
const viewportChangedTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Lifecycle resets are not user actions: a reset within the debounce window
// cancels the pending report, since the fire-time state cannot distinguish a
// reset Fit viewport from one the user chose.
function cancelViewportChanged(panelId: string) {
  const timer = viewportChangedTimers.get(panelId);
  if (timer != null) {
    clearTimeout(timer);
    viewportChangedTimers.delete(panelId);
  }
}

function scheduleViewportChanged(
  panelId: string,
  get: () => CompareViewportState
) {
  clearTimeout(viewportChangedTimers.get(panelId));
  viewportChangedTimers.set(
    panelId,
    setTimeout(() => {
      viewportChangedTimers.delete(panelId);
      const viewport = get().viewportsByPanelId[panelId];
      // A project reset may have dropped the panel while the timer ran.
      if (viewport == null) {
        return;
      }
      mediaCompareViewportChanged({
        zoom: viewport.zoom,
        panned: viewport.center.x !== 50 || viewport.center.y !== 50,
      });
    }, VIEWPORT_CHANGED_DEBOUNCE_MS)
  );
}

export const useCompareViewportStore = create<CompareViewportState>()((
  set,
  get
) => {
  const update = (panelId: string, patch: Partial<CompareViewport>): void => {
    set(state => ({
      viewportsByPanelId: {
        ...state.viewportsByPanelId,
        [panelId]: {...selectViewport(panelId)(state), ...patch},
      },
    }));
  };

  return {
    viewportsByPanelId: {},
    activePanelId: null,
    activePanelOwnerId: null,
    projectKey: null,
    setActivePanel: (panelId, ownerId) => {
      if (selectViewport(panelId)(get()).isSynced) {
        set({activePanelId: panelId, activePanelOwnerId: ownerId});
      }
    },
    releaseActivePanel: ownerId => {
      if (get().activePanelOwnerId === ownerId) {
        set({activePanelId: null, activePanelOwnerId: null});
      }
    },
    setZoom: (panelId, zoom) => {
      const viewport = selectViewport(panelId)(get());
      // Only synced panels own a shared zoom; a stale write (e.g. after a
      // scope reset or fan-out change) must not recreate an entry.
      if (!viewport.isSynced || viewport.zoom === zoom) {
        return;
      }
      // Fit resets the pan so the next zoom starts centered.
      update(panelId, {
        zoom,
        ...(zoom === 'fit' ? {center: CENTERED_VIEWPORT} : {}),
      });
      scheduleViewportChanged(panelId, get);
    },
    setViewportInfo: (panelId, info) => {
      const viewport = selectViewport(panelId)(get());
      if (
        viewport.isEligible === info.isEligible &&
        viewport.isSynced === info.isSynced
      ) {
        return;
      }
      update(panelId, info);
      if (info.isSynced === false) {
        cancelViewportChanged(panelId);
        if (get().activePanelId === panelId) {
          set({activePanelId: null, activePanelOwnerId: null});
        }
      }
    },
    setCenter: (panelId, center) => {
      const viewport = selectViewport(panelId)(get());
      if (
        !viewport.isSynced ||
        (viewport.center.x === center.x && viewport.center.y === center.y)
      ) {
        return;
      }
      update(panelId, {center});
      scheduleViewportChanged(panelId, get);
    },
    resetViewport: panelId => {
      cancelViewportChanged(panelId);
      const viewport = get().viewportsByPanelId[panelId];
      if (
        viewport != null &&
        (viewport.zoom !== 'fit' || viewport.center !== CENTERED_VIEWPORT)
      ) {
        update(panelId, {zoom: 'fit', center: CENTERED_VIEWPORT});
      }
    },
    resetProject: projectKey => {
      if (get().projectKey !== projectKey) {
        for (const panelId of viewportChangedTimers.keys()) {
          cancelViewportChanged(panelId);
        }
        set({
          projectKey,
          viewportsByPanelId: {},
          activePanelId: null,
          activePanelOwnerId: null,
        });
      }
    },
  };
});
